import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, extractApiError } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useTheme } from "@/contexts/ThemeContext";
import { ensurePushSubscribed, unsubscribePush, pushTest } from "@/lib/push";

export default function Profile() {
  const { user, refreshUser, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  const [name, setName] = useState(user?.name || "");
  const [organization, setOrganization] = useState(user?.organization || "");
  const [country, setCountry] = useState(user?.country || "");
  const [saving, setSaving] = useState(false);

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me", { name, organization, country, language: lang, theme });
      await refreshUser();
      toast.success(t("common.success"));
    } catch (e) {
      toast.error(extractApiError(e));
    } finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (!currentPw || !newPw) return;
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
      toast.success(t("common.success"));
      setCurrentPw(""); setNewPw("");
    } catch (e) {
      toast.error(extractApiError(e));
    } finally { setPwSaving(false); }
  };

  const deleteAccount = async () => {
    try {
      await api.delete("/users/me");
      await logout();
      navigate("/");
    } catch (e) { toast.error(extractApiError(e)); }
  };

  const enableNotifications = async () => {
    try {
      const ok = await ensurePushSubscribed();
      if (ok) toast.success(t("common.success"));
      else toast.error(t("profile.notif.denied"));
    } catch (e) { toast.error(extractApiError(e)); }
  };
  const testNotification = async () => {
    try {
      const { data } = await pushTest();
      toast.success(`${t("common.success")}: ${data.delivered}/${data.total}`);
    } catch (e) { toast.error(extractApiError(e)); }
  };

  return (
    <AppLayout>
      <h1 className="font-display font-extrabold text-4xl tracking-tight mb-6">{t("profile.title")}</h1>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-border">
          <CardHeader><CardTitle className="font-display">{t("profile.personal")}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>{t("auth.email")}</Label>
              <Input value={user?.email || ""} disabled className="h-11 mt-1" data-testid="profile-email-display" />
            </div>
            <div>
              <Label>{t("auth.name")}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="h-11 mt-1" data-testid="profile-name-input" />
            </div>
            <div>
              <Label>{t("profile.org")}</Label>
              <Input value={organization} onChange={(e) => setOrganization(e.target.value)} className="h-11 mt-1"
                placeholder="Ex: Université FHB" data-testid="profile-org-input" />
            </div>
            <div>
              <Label>{t("profile.country")}</Label>
              <Input value={country} onChange={(e) => setCountry(e.target.value)} className="h-11 mt-1"
                placeholder="Ex: Côte d'Ivoire" data-testid="profile-country-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("profile.language")}</Label>
                <Select value={lang} onValueChange={setLang}>
                  <SelectTrigger className="h-11 mt-1" data-testid="profile-lang-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fr">Français</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("profile.theme")}</Label>
                <Select value={theme} onValueChange={setTheme}>
                  <SelectTrigger className="h-11 mt-1" data-testid="profile-theme-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">{t("profile.theme.light")}</SelectItem>
                    <SelectItem value="dark">{t("profile.theme.dark")}</SelectItem>
                    <SelectItem value="system">{t("profile.theme.system")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={saveProfile} disabled={saving} data-testid="profile-save-btn">
              {saving ? t("common.loading") : t("profile.save")}
            </Button>
          </CardContent>
        </Card>

        {user?.provider === "password" && (
          <Card className="border-border">
            <CardHeader><CardTitle className="font-display">{t("profile.password")}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{t("profile.password.current")}</Label>
                <Input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
                  className="h-11 mt-1" data-testid="profile-current-pw" />
              </div>
              <div>
                <Label>{t("profile.password.new")}</Label>
                <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
                  className="h-11 mt-1" data-testid="profile-new-pw" />
              </div>
              <Button onClick={changePassword} disabled={pwSaving} data-testid="profile-pw-update-btn">
                {t("profile.password.update")}
              </Button>
            </CardContent>
          </Card>
        )}

        <Card className="border-destructive/40 lg:col-span-2">
          <CardHeader><CardTitle className="font-display text-destructive">{t("profile.danger")}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2 pb-4 border-b border-border">
              <Button variant="outline" onClick={enableNotifications} data-testid="profile-push-enable-btn">
                {t("profile.notif.enable")}
              </Button>
              <Button variant="ghost" size="sm" onClick={testNotification} data-testid="profile-push-test-btn">
                {t("profile.notif.test")}
              </Button>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" data-testid="profile-delete-btn">{t("profile.delete")}</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("profile.deleteConfirm")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    Toutes vos données seront supprimées définitivement.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="delete-cancel-btn">{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={deleteAccount} data-testid="delete-confirm-btn">{t("common.confirm")}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
